import React, { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, Loader2, RefreshCw, X } from 'lucide-react';
import {
  CAMPOS_TEMPLATE,
  camposDoTemplate,
  preencherTemplate,
  renderizarSite,
  templateDeFallback,
  templatePorId,
  TEMPLATES,
  valoresDoCliente,
  type CampoTemplate,
  type ValoresTemplate,
} from '@nyroh/contracts';

import { api, CDN_ASSETS, urlDaImagem } from '../lib/api';
import { BibliotecaDeImagens } from './BibliotecaDeImagens';

/**
 * ============================================================================
 * TROCAR O MODELO DO SITE, PELO PAINEL
 * ============================================================================
 *
 * O painel tinha um editor só: o VISUAL, que mexe no HTML já publicado clicando
 * em cima dele. Ele serve para ajuste fino e não serve para o que esta tela faz
 * — trocar o modelo inteiro e repreencher os campos.
 *
 * A tela existia na FÁBRICA, e só lá. Quem opera o painel via o site pronto sem
 * jeito de dizer "esse modelo não combinou, quero o Editorial".
 *
 * A PRÉVIA É O RENDERIZADOR DE VERDADE
 *
 * `preencherTemplate` + `renderizarSite` são as MESMAS funções que o worker
 * chama para publicar. Não é uma aproximação da página: é a página. Divergência
 * entre prévia e publicado já custou caro neste repositório, e a forma de não
 * repetir é não ter duas implementações.
 */

interface Props {
  clienteId: string;
  onFechar: () => void;
  onPublicado?: () => void;
}

interface ClienteDaApi {
  id: string;
  nome: string;
  slug: string;
  categoria?: string | null;
  telefone?: string | null;
  email?: string | null;
  endereco?: string | null;
  dadosNegocio?: {
    resumo?: string | null;
    categorias?: string[];
    palavrasChaveSeo?: string[];
    horarios?: unknown;
  } | null;
}

const ESTILO_INPUT =
  'w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none ' +
  'focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400';

export const EditorDeTemplate: React.FC<Props> = ({ clienteId, onFechar, onPublicado }) => {
  const [cliente, setCliente] = useState<ClienteDaApi | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [templateId, setTemplateId] = useState('');
  const [valores, setValores] = useState<ValoresTemplate>({});
  const [publicando, setPublicando] = useState(false);
  const [publicado, setPublicado] = useState(false);
  const [bibliotecaPara, setBibliotecaPara] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const c = (await api.chamar('clientes.obter', {
          params: { id: clienteId },
        })) as ClienteDaApi;
        if (!vivo) return;
        setCliente(c);
        setValores(valoresDoCliente(c));
        // O mesmo critério do worker: categoria quando ela diz algo, sorteio
        // semeado quando não diz. Assim a tela abre no que seria publicado.
        setTemplateId(templateDeFallback(c.id, c.categoria, c.nome).id);
      } catch (e) {
        if (vivo) setErro(e instanceof Error ? e.message : 'Não consegui carregar o cliente.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [clienteId]);

  const template = useMemo(
    () => templatePorId(templateId) ?? TEMPLATES[TEMPLATES.length - 1]!,
    [templateId],
  );
  const campos = useMemo(() => camposDoTemplate(template), [template]);

  const html = useMemo(() => {
    try {
      return renderizarSite(preencherTemplate(template, valores), { baseCdn: CDN_ASSETS });
    } catch (e) {
      console.error('[editor-template] falha ao montar a prévia:', e);
      return '<p style="font:14px system-ui;padding:24px">Não consegui montar a prévia.</p>';
    }
  }, [template, valores]);

  const definir = (chave: string, valor: string) =>
    setValores((v) => ({ ...v, [chave]: valor }));

  const publicar = async () => {
    setPublicando(true);
    setErro(null);
    try {
      const spec = preencherTemplate(template, valores);
      const sites = await api.chamar('sites.listarPorCliente', { params: { id: clienteId } });
      const site =
        sites[0] ??
        (await api.chamar('sites.criar', {
          corpo: { clienteId, slug: cliente!.slug, spec },
        }));

      await api.chamar('sites.publicar', {
        params: { id: site.id },
        corpo: { spec, geradoPor: `template:${template.id}`, apenasRascunho: false },
      });
      setPublicado(true);
      onPublicado?.();
    } catch (e) {
      console.error('[editor-template] falha ao publicar:', e);
      setErro(e instanceof Error ? e.message : 'Não consegui publicar.');
    } finally {
      setPublicando(false);
    }
  };

  if (carregando) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loader2 className="animate-spin text-gray-400" size={28} />
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-white p-8 text-center">
        <p className="text-sm text-gray-600">{erro ?? 'Cliente não encontrado.'}</p>
        <button onClick={onFechar} className="text-sm font-semibold text-gray-900 underline">
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white">
      <header className="flex items-center gap-4 border-b border-gray-200 px-5 py-3">
        <div className="mr-auto">
          <h1 className="text-sm font-bold text-gray-900">Modelo do site</h1>
          <p className="text-xs text-gray-500">{cliente.nome}</p>
        </div>

        {erro && <span className="text-xs text-red-600">{erro}</span>}

        <button
          onClick={() => void publicar()}
          disabled={publicando}
          className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {publicando && <Loader2 size={14} className="animate-spin" />}
          {publicado ? 'Publicado' : 'Publicar agora'}
        </button>
        <button
          onClick={onFechar}
          aria-label="Fechar"
          className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-900"
        >
          <X size={18} />
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        <div className="w-[400px] shrink-0 overflow-y-auto border-r border-gray-200">
          <div className="border-b border-gray-100 p-5">
            <Rotulo campo={{ chave: 'template', rotulo: 'Modelo', tipo: 'curto' }} />
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className={ESTILO_INPUT}
            >
              {TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
              {template.descricao}
            </p>

            <button
              onClick={() => {
                setTemplateId(templateDeFallback(cliente.id, cliente.categoria, cliente.nome).id);
                setValores(valoresDoCliente(cliente));
              }}
              className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-gray-500 hover:text-gray-900"
            >
              <RefreshCw size={12} /> Voltar ao que veio do cadastro
            </button>
            <button
              onClick={() => setBibliotecaPara('')}
              className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-gray-500 hover:text-gray-900"
            >
              <ImageIcon size={12} /> Subir e organizar imagens
            </button>
          </div>

          <div className="space-y-4 p-5">
            {campos.map((c) => (
              <div key={c.chave}>
                <Rotulo campo={c} />

                {c.tipo === 'imagem' ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setBibliotecaPara(c.chave)}
                      className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold hover:bg-gray-50"
                    >
                      <ImageIcon size={13} />
                      {valores[c.chave] ? 'Trocar' : 'Escolher imagem'}
                    </button>
                    {valores[c.chave] && (
                      <>
                        <img
                          src={urlDaImagem(valores[c.chave]!)}
                          alt=""
                          className="h-10 w-10 rounded border border-gray-200 object-cover"
                        />
                        <button
                          onClick={() => definir(c.chave, '')}
                          className="text-[11px] text-gray-400 hover:text-red-600"
                        >
                          tirar
                        </button>
                      </>
                    )}
                  </div>
                ) : c.tipo === 'longo' ? (
                  <textarea
                    value={valores[c.chave] ?? ''}
                    onChange={(e) => definir(c.chave, e.target.value)}
                    rows={3}
                    className={ESTILO_INPUT}
                  />
                ) : (
                  <input
                    type={c.tipo === 'email' ? 'email' : 'text'}
                    value={valores[c.chave] ?? ''}
                    onChange={(e) => definir(c.chave, e.target.value)}
                    className={ESTILO_INPUT}
                  />
                )}
              </div>
            ))}

            <p className="border-t border-gray-100 pt-3 text-[11px] leading-relaxed text-gray-400">
              Campo em branco não vira espaço vazio no site: a seção inteira sai fora. Só o
              nome do negócio é indispensável.
            </p>
          </div>
        </div>

        {/*
          `srcDoc` e não `src`: a prévia é HTML montado aqui, que nunca foi ao
          servidor. `sandbox` sem `allow-same-origin` mantém a página numa origem
          opaca — o conteúdo vem do template, mas os textos vêm de campo livre, e
          uma prévia não precisa de acesso ao painel que a hospeda.
        */}
        <iframe
          title="Prévia do site"
          srcDoc={html}
          sandbox="allow-scripts"
          className="min-w-0 flex-1 bg-gray-100"
        />
      </div>

      {bibliotecaPara !== null && (
        <BibliotecaDeImagens
          clienteId={clienteId}
          campoAlvo={bibliotecaPara || null}
          chaveAtual={bibliotecaPara ? valores[bibliotecaPara] : undefined}
          aoEscolher={(chaveR2) => {
            if (bibliotecaPara) definir(bibliotecaPara, chaveR2);
            setBibliotecaPara(null);
          }}
          aoFechar={() => setBibliotecaPara(null)}
        />
      )}
    </div>
  );
};

const Rotulo: React.FC<{ campo: CampoTemplate }> = ({ campo }) => (
  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">
    {campo.rotulo}
    {campo.obrigatorio && <span className="ml-1 text-red-500">*</span>}
    {campo.ajuda && (
      <span className="ml-2 font-normal normal-case tracking-normal text-gray-300">
        {campo.ajuda}
      </span>
    )}
  </label>
);

/** Reexportado para quem quiser montar um formulário com o mesmo conjunto. */
export { CAMPOS_TEMPLATE };
